import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { showCustomAlert } from "./utils/alert";
import DashboardPage from "./pages/DashboardPage";
import MedicationsPage from "./pages/MedicationsPage";
import LabReportsPage from "./pages/LabReportsPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import VitalsPage from "./pages/VitalsPage";
import LoginPage from "./pages/LoginPage";
import RegistrationPage from "./pages/RegistrationPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import { useAuth } from "./contexts/AuthContext";
import ProfilesPage from "./pages/ProfilesPage";
import CareProfileEditPage from "./pages/CareProfileEditPage";
import CareProfileDetailsPage from "./pages/CareProfileDetailsPage";
import SeniorProfileSummary from "./components/SeniorProfileSummary";
import { calculateAge } from "./utils/ageCalculator";
import VitalsSettingsPage from "./pages/VitalsSettingsPage";
import CreateProfilePage from "./pages/CreateProfilePage";
import {
  FaBars,
  FaTachometerAlt,
  FaFileMedicalAlt,
  FaCapsules,
  FaCalendarCheck,
  FaStickyNote,
  FaAngleDown,
  FaAngleUp,
  FaSignOutAlt,
  FaHeartbeat,
  FaUserCog,
  FaUser,
  FaStar,
  FaRegStar,
  FaSearch,
  FaRegClock,
} from "react-icons/fa";
import CareTeamPage from "./pages/CareTeamPage";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import NotesIncidentsPage from "./pages/NotesIncidentsPage";
import DailyCareLogPage from "./pages/DailyCareLogPage";
import OfflineStatusBanner from "./components/OfflineStatusBanner";

// This is your global alert override
window.alert = showCustomAlert;

const App = () => {
  const {
    currentUser,
    loading,
    activeCareProfile,
    logout,
    ownedProfiles,
    sharedProfiles,
    selectActiveCareProfile,
  } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [pinnedIds, setPinnedIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("pinnedProfileIds")) || [];
    } catch {
      return [];
    }
  });
  const [profileSearch, setProfileSearch] = useState("");

  /* ---------------- Remember last in-app route ---------------- */
  const location = useLocation();
  useEffect(() => {
    // Exclude auth pages from tracking
    const excluded = [
      "/login",
      "/register",
      "/forgot-password",
      "/verify-email",
    ];
    if (!excluded.includes(location.pathname)) {
      localStorage.setItem("lastRoute", location.pathname);
    }
  }, [location.pathname]);
  const lastRoute = localStorage.getItem("lastRoute") || "/profiles";
  const menuRef = useRef(null);

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const augmentedActiveCareProfile = activeCareProfile
    ? {
        ...activeCareProfile,
        age: calculateAge(activeCareProfile.dob),
      }
    : null;

  const handleLogout = async () => {
    if (window.confirm("Are you sure you want to log out?")) {
      try {
        await logout();
      } catch (error) {
        console.error("Logout failed in App.js", error);
        showCustomAlert("Failed to log out. Please try again.");
      }
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const toggleSubMenu = (itemName) =>
    setOpenSubMenu(openSubMenu === itemName ? null : itemName);
  const isSubMenuOpen = (itemName) => openSubMenu === itemName;
  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (isSidebarOpen && window.innerWidth < 1024) {
        const sidebar = document.getElementById("sidebar");
        const toggleButton = document.getElementById("sidebar-toggle");
        if (
          sidebar &&
          !sidebar.contains(event.target) &&
          toggleButton &&
          !toggleButton.contains(event.target)
        ) {
          setIsSidebarOpen(false);
        }
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isSidebarOpen]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Loading profiles...</div>
      </div>
    );
  }

  let menuItems = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: <FaTachometerAlt />,
      component: DashboardPage,
    },
    {
      name: "Daily Care Log",
      path: "/daily-care-log",
      icon: <FaRegClock />,
      component: DailyCareLogPage,
    },
    {
      name: "Medicine Tracker",
      icon: <FaCapsules />,
      path: "/medications",
    },
    {
      name: "Health Documents",
      path: "/lab-reports",
      icon: <FaFileMedicalAlt />,
      component: LabReportsPage,
    },
    {
      name: "Appointments",
      path: "/appointments",
      icon: <FaCalendarCheck />,
      component: AppointmentsPage,
    },
    {
      name: "Vitals",
      path: "/vitals",
      icon: <FaHeartbeat />,
      component: VitalsPage,
    },
    {
      name: "Notes & Health Events",
      path: "/notes-incidents",
      icon: <FaStickyNote />,
      component: NotesIncidentsPage,
    },
    {
      name: "Family Profiles",
      path: "/profiles",
      icon: <FaUserCog />,
      component: ProfilesPage,
    },
  ];

  if (!currentUser) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegistrationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  // Force email verification before allowing access to the app
  if (currentUser && !currentUser.emailVerified) {
    return (
      <Routes>
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="*" element={<Navigate to="/verify-email" />} />
      </Routes>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100 font-sans">
      <aside
        id="sidebar"
        className={`bg-gray-800 text-white w-64 fixed inset-y-0 left-0 p-5 space-y-6 transform ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 z-30 flex-shrink-0 overflow-y-auto`}
      >
        <div className="flex items-center justify-center mb-6">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="MILA Care Logo"
            className="h-10 w-10 mr-3"
          />
          <h1 className="text-3xl font-extrabold text-[#0e82c0]">MILA Care</h1>
        </div>

        <nav>
          <ul>
            {menuItems.map((item) => (
              <li key={item.name} className="mb-2">
                {item.subItems ? (
                  <>
                    <button
                      onClick={() => toggleSubMenu(item.name)}
                      className="w-full flex justify-between items-center text-lg text-gray-300 hover:text-white hover:bg-gray-700 px-4 py-3 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                      aria-expanded={isSubMenuOpen(item.name)}
                    >
                      <span className="flex items-center">
                        <span className="w-5">{item.icon}</span>
                        <span className="ml-3">{item.name}</span>
                      </span>
                      {isSubMenuOpen(item.name) ? (
                        <FaAngleUp />
                      ) : (
                        <FaAngleDown />
                      )}
                    </button>
                    {isSubMenuOpen(item.name) && (
                      <ul className="pl-8 mt-1 space-y-1">
                        {item.subItems.map((subItem) => (
                          <li key={subItem.name}>
                            <Link
                              to={subItem.path}
                              onClick={handleLinkClick}
                              className="block text-sm text-gray-400 hover:text-white px-3 py-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                            >
                              {subItem.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    className="w-full flex items-center text-lg text-gray-300 hover:text-white hover:bg-gray-700 px-4 py-3 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                  >
                    <span className="w-5">{item.icon}</span>
                    <span className="ml-3">{item.name}</span>
                  </Link>
                )}
              </li>
            ))}
            <li className="mb-2">
              <button
                onClick={handleLogout}
                className="w-full flex items-center text-lg text-gray-300 hover:text-white hover:bg-red-600 px-4 py-3 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
              >
                <span className="w-5">
                  <FaSignOutAlt />
                </span>
                <span className="ml-3">Logout</span>
              </button>
            </li>
          </ul>
        </nav>
      </aside>

      <div className="relative flex flex-col flex-1 w-full min-w-0">
        <header className="sticky top-0 bg-white shadow-sm z-20 px-4 py-3 sm:px-6 border-b">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              id="sidebar-toggle"
              onClick={toggleSidebar}
              className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 lg:hidden"
              aria-label="Toggle sidebar"
            >
              <FaBars size={24} />
            </button>
            {activeCareProfile && (
              <div className="w-full relative" ref={menuRef}>
                <button
                  onClick={() => setShowProfileMenu((prev) => !prev)}
                  className="w-full rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  <SeniorProfileSummary profile={augmentedActiveCareProfile} />
                </button>
                {showProfileMenu && (
                  <div className="absolute left-0 mt-2 w-full bg-white shadow-lg rounded-xl z-30 max-h-[70vh] overflow-auto border p-2 space-y-2">
                    {/* Search */}
                    <div className="flex items-center gap-2 px-2">
                      <FaSearch className="text-gray-400" />
                      <input
                        type="text"
                        value={profileSearch}
                        onChange={(e) => setProfileSearch(e.target.value)}
                        placeholder="Search family profiles"
                        className="flex-1 text-sm outline-none"
                      />
                    </div>
                    <hr />
                    {/* List */}
                    {(() => {
                      const all = [...ownedProfiles, ...sharedProfiles];
                      const pinned = pinnedIds
                        .map((id) => all.find((p) => p.id === id))
                        .filter(Boolean);
                      const others = all.filter(
                        (p) => !pinnedIds.includes(p.id),
                      );
                      const list = [...pinned, ...others].filter((p) =>
                        p.profileName
                          .toLowerCase()
                          .includes(profileSearch.toLowerCase()),
                      );
                      return list.map((p) => {
                        const isPinned = pinnedIds.includes(p.id);
                        return (
                          <div
                            key={p.id}
                            className={`flex items-center justify-between px-4 py-2 rounded hover:bg-gray-100 ${
                              p.id === activeCareProfile.id ? "bg-gray-100" : ""
                            }`}
                          >
                            <button
                              onClick={() => {
                                selectActiveCareProfile(p);
                                setShowProfileMenu(false);
                              }}
                              className="flex flex-1 items-center gap-3 text-left"
                            >
                              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100">
                                {p.profilePictureURL ? (
                                  <img
                                    src={p.profilePictureURL}
                                    alt={p.profileName || "Family profile"}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <FaUser className="text-sm text-slate-500" />
                                )}
                              </span>
                              <span>{p.profileName}</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const newIds = isPinned
                                  ? pinnedIds.filter((id) => id !== p.id)
                                  : [p.id, ...pinnedIds];
                                setPinnedIds(newIds);
                                localStorage.setItem(
                                  "pinnedProfileIds",
                                  JSON.stringify(newIds),
                                );
                              }}
                              className="ml-2 text-yellow-500"
                              aria-label={isPinned ? "Unpin" : "Pin"}
                            >
                              {isPinned ? <FaStar /> : <FaRegStar />}
                            </button>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        </header>

        <OfflineStatusBanner />

        <main
          id="main-content"
          className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 lg:p-10"
        >
          <ErrorBoundary>
            <Routes>
              {/* Redirect root to last visited in-app page (defaults to /profiles) */}
              <Route path="/" element={<Navigate to={lastRoute} />} />
              <Route
                path="/create-profile"
                element={
                  <ProtectedRoute>
                    <CreateProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vitals"
                element={
                  <ProtectedRoute>
                    <VitalsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/daily-care-log"
                element={
                  <ProtectedRoute>
                    <DailyCareLogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medications/*"
                element={
                  <ProtectedRoute>
                    <MedicationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lab-reports"
                element={
                  <ProtectedRoute>
                    <LabReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/appointments"
                element={
                  <ProtectedRoute>
                    <AppointmentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notes-incidents/*"
                element={
                  <ProtectedRoute>
                    <NotesIncidentsPage />
                  </ProtectedRoute>
                }
              />
              {/* legacy routes redirect */}
              <Route
                path="/incident-reporting"
                element={<Navigate to="/notes-incidents" replace />}
              />
              <Route
                path="/notes"
                element={<Navigate to="/notes-incidents" replace />}
              />
              <Route
                path="/care-team"
                element={
                  <ProtectedRoute>
                    <CareTeamPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profiles"
                element={
                  <ProtectedRoute>
                    <ProfilesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profiles/new"
                element={
                  <ProtectedRoute>
                    <CareProfileEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profiles/edit/:profileId"
                element={
                  <ProtectedRoute>
                    <CareProfileEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profiles/view/:profileId"
                element={
                  <ProtectedRoute>
                    <CareProfileDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profiles/:profileId/vitals-settings"
                element={
                  <ProtectedRoute>
                    <VitalsSettingsPage />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};

export default App;
